export const BUTTON_VISUAL_VARIANTS = {
  filled: 'filled',
  brand: 'brand',
  accent: 'accent',
  hero: 'hero',
  minimal: 'minimal',
  premium: 'premium',
  pastel: 'pastel',
  gradient: 'gradient',
  glass: 'glass',
  secondary: 'secondary',
  outline: 'outline',
  ghost: 'ghost',
  soft: 'soft',
  dark: 'dark',
  inverted: 'inverted',
  link: 'link',
  pill: 'pill',
} as const;

export type ButtonVisualVariant = (typeof BUTTON_VISUAL_VARIANTS)[keyof typeof BUTTON_VISUAL_VARIANTS];

export const SOCIAL_VISUAL_VARIANTS = {
  simple: 'simple',
  minimal: 'minimal',
  outline: 'outline',
  circles: 'circles',
  filled: 'filled',
  brand: 'brand',
  dark: 'dark',
  pill: 'pill',
  stacked: 'stacked',
} as const;

export type SocialVisualVariant = (typeof SOCIAL_VISUAL_VARIANTS)[keyof typeof SOCIAL_VISUAL_VARIANTS];
export type SocialSizingMode = 'hug' | 'stretch';
export type SocialOrientation = 'row' | 'stack';
export type SocialAlignX = 'left' | 'center' | 'right';
export type SocialAlignY = 'top' | 'center' | 'bottom';
export type SocialIconSize = 'sm' | 'md' | 'lg' | 'xl';

export const SOCIAL_ICON_SIZE_PX: Record<SocialIconSize, number> = {
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
};

export const MEDIA_FRAME_VARIANTS = {
  default: 'default',
  square: 'square',
  card: 'card',
  editorial: 'editorial',
  premium: 'premium',
  polaroid: 'polaroid',
} as const;

export type MediaFrameVariant = (typeof MEDIA_FRAME_VARIANTS)[keyof typeof MEDIA_FRAME_VARIANTS];

export const TEXT_SURFACE_VARIANTS = {
  default: 'default',
  card: 'card',
  feature: 'feature',
  testimonial: 'testimonial',
  cta: 'cta',
  premium: 'premium',
  marketing: 'marketing',
} as const;

export type TextSurfaceVariant = (typeof TEXT_SURFACE_VARIANTS)[keyof typeof TEXT_SURFACE_VARIANTS];

export function resolveButtonVisualVariant(value: unknown): ButtonVisualVariant {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === BUTTON_VISUAL_VARIANTS.brand) return BUTTON_VISUAL_VARIANTS.brand;
  if (normalized === BUTTON_VISUAL_VARIANTS.accent) return BUTTON_VISUAL_VARIANTS.accent;
  if (normalized === BUTTON_VISUAL_VARIANTS.hero) return BUTTON_VISUAL_VARIANTS.hero;
  if (normalized === BUTTON_VISUAL_VARIANTS.minimal) return BUTTON_VISUAL_VARIANTS.minimal;
  if (normalized === BUTTON_VISUAL_VARIANTS.premium) return BUTTON_VISUAL_VARIANTS.premium;
  if (normalized === BUTTON_VISUAL_VARIANTS.pastel) return BUTTON_VISUAL_VARIANTS.pastel;
  if (normalized === BUTTON_VISUAL_VARIANTS.gradient) return BUTTON_VISUAL_VARIANTS.gradient;
  if (normalized === BUTTON_VISUAL_VARIANTS.glass) return BUTTON_VISUAL_VARIANTS.glass;
  if (normalized === BUTTON_VISUAL_VARIANTS.pill) return BUTTON_VISUAL_VARIANTS.pill;
  if (normalized === BUTTON_VISUAL_VARIANTS.dark) return BUTTON_VISUAL_VARIANTS.dark;
  if (normalized === BUTTON_VISUAL_VARIANTS.secondary) return BUTTON_VISUAL_VARIANTS.secondary;
  if (normalized === BUTTON_VISUAL_VARIANTS.outline) return BUTTON_VISUAL_VARIANTS.outline;
  if (normalized === BUTTON_VISUAL_VARIANTS.ghost) return BUTTON_VISUAL_VARIANTS.ghost;
  if (normalized === BUTTON_VISUAL_VARIANTS.soft) return BUTTON_VISUAL_VARIANTS.soft;
  if (normalized === BUTTON_VISUAL_VARIANTS.inverted) return BUTTON_VISUAL_VARIANTS.inverted;
  if (normalized === BUTTON_VISUAL_VARIANTS.link) return BUTTON_VISUAL_VARIANTS.link;
  return BUTTON_VISUAL_VARIANTS.filled;
}

export function getButtonVariantClasses(variant: ButtonVisualVariant): string {
  switch (variant) {
    case 'brand':
      return 'rounded-full border border-blue-700 bg-blue-600 text-white shadow-sm hover:bg-blue-500';
    case 'accent':
      return 'rounded-full border border-amber-400 bg-amber-300 text-stone-950 shadow-sm hover:bg-amber-200';
    case 'hero':
      return 'rounded-full border border-white/20 bg-stone-950 text-white shadow-md hover:bg-stone-900';
    case 'minimal':
      return 'bg-transparent text-stone-900 underline decoration-stone-400 underline-offset-4 hover:text-stone-700';
    case 'premium':
      return 'rounded-full border border-stone-200 bg-gradient-to-r from-stone-900 via-stone-800 to-amber-700 text-white shadow-md hover:from-stone-800 hover:to-amber-600';
    case 'pastel':
      return 'rounded-full border border-rose-200 bg-rose-100 text-rose-900 shadow-sm hover:bg-rose-200';
    case 'gradient':
      return 'rounded-full border border-transparent bg-gradient-to-r from-blue-600 via-violet-500 to-fuchsia-500 text-white shadow-md hover:opacity-95';
    case 'glass':
      return 'rounded-full border border-white/40 bg-white/20 text-stone-900 backdrop-blur-md shadow-md hover:bg-white/30';
    case 'outline':
      return 'rounded-full border border-stone-400 bg-white text-stone-900 hover:bg-stone-50';
    case 'ghost':
      return 'rounded-full bg-transparent text-stone-900 hover:bg-stone-100';
    case 'soft':
      return 'rounded-full bg-stone-200 text-stone-900 hover:bg-stone-300';
    case 'secondary':
      return 'rounded-full bg-stone-700 text-white hover:bg-stone-600';
    case 'inverted':
      return 'rounded-full border border-white/20 bg-stone-900 text-white hover:bg-stone-800';
    case 'link':
      return 'bg-transparent text-blue-700 underline underline-offset-4 hover:text-blue-600';
    case 'dark':
    case 'pill':
      return 'rounded-full bg-stone-900 text-white hover:bg-stone-800';
    default:
      return 'rounded-full border border-blue-700 bg-blue-600 text-white hover:bg-blue-500';
  }
}

export function getTextSurfaceClasses(variant: TextSurfaceVariant): string {
  switch (variant) {
    case 'premium':
      return 'rounded-[28px] border border-stone-200 bg-gradient-to-br from-stone-950 via-stone-900 to-amber-800 shadow-md';
    case 'marketing':
      return 'rounded-[28px] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-amber-50 shadow-sm';
    case 'feature':
      return 'rounded-2xl border border-amber-200 bg-amber-50/85 shadow-sm';
    case 'testimonial':
      return 'rounded-2xl border border-stone-200 bg-white shadow-sm';
    case 'cta':
      return 'rounded-2xl bg-stone-900 text-white shadow-md';
    case 'card':
      return 'rounded-2xl border border-stone-200 bg-white shadow-sm';
    default:
      return '';
  }
}

export function getTextSurfaceToneClass(variant: TextSurfaceVariant): string {
  return variant === 'cta' || variant === 'premium' ? 'text-white' : 'text-stone-900';
}

export function resolveTextSurfaceVariant(value: unknown): TextSurfaceVariant {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === TEXT_SURFACE_VARIANTS.card) return TEXT_SURFACE_VARIANTS.card;
  if (normalized === TEXT_SURFACE_VARIANTS.feature) return TEXT_SURFACE_VARIANTS.feature;
  if (normalized === TEXT_SURFACE_VARIANTS.testimonial) return TEXT_SURFACE_VARIANTS.testimonial;
  if (normalized === TEXT_SURFACE_VARIANTS.cta) return TEXT_SURFACE_VARIANTS.cta;
  if (normalized === TEXT_SURFACE_VARIANTS.premium) return TEXT_SURFACE_VARIANTS.premium;
  if (normalized === TEXT_SURFACE_VARIANTS.marketing) return TEXT_SURFACE_VARIANTS.marketing;
  return TEXT_SURFACE_VARIANTS.default;
}

export function resolveSocialVisualVariant(value: unknown): SocialVisualVariant {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === SOCIAL_VISUAL_VARIANTS.minimal) return SOCIAL_VISUAL_VARIANTS.minimal;
  if (normalized === SOCIAL_VISUAL_VARIANTS.simple) return SOCIAL_VISUAL_VARIANTS.simple;
  if (normalized === SOCIAL_VISUAL_VARIANTS.circles) return SOCIAL_VISUAL_VARIANTS.circles;
  if (normalized === SOCIAL_VISUAL_VARIANTS.filled) return SOCIAL_VISUAL_VARIANTS.filled;
  if (normalized === SOCIAL_VISUAL_VARIANTS.brand) return SOCIAL_VISUAL_VARIANTS.brand;
  if (normalized === SOCIAL_VISUAL_VARIANTS.dark) return SOCIAL_VISUAL_VARIANTS.dark;
  if (normalized === SOCIAL_VISUAL_VARIANTS.pill) return SOCIAL_VISUAL_VARIANTS.pill;
  if (normalized === SOCIAL_VISUAL_VARIANTS.stacked) return SOCIAL_VISUAL_VARIANTS.stacked;
  return SOCIAL_VISUAL_VARIANTS.outline;
}

export function resolveSocialSizingMode(value: unknown): SocialSizingMode {
  return value === 'stretch' ? 'stretch' : 'hug';
}

export function resolveSocialOrientation(value: unknown, variant?: SocialVisualVariant): SocialOrientation {
  if (value === 'stack' || variant === 'stacked') return 'stack';
  return 'row';
}

export function resolveSocialAlignX(value: unknown): SocialAlignX {
  if (value === 'left' || value === 'right') return value;
  return 'center';
}

export function resolveSocialAlignY(value: unknown): SocialAlignY {
  if (value === 'top' || value === 'bottom') return value;
  return 'center';
}

export function resolveSocialIconSize(value: unknown): SocialIconSize {
  if (value === 'sm' || value === 'lg' || value === 'xl') return value;
  return 'md';
}

export function getSocialContainerClasses(variant: SocialVisualVariant): string {
  switch (variant) {
    case 'minimal':
      return 'relative flex h-full w-full items-center gap-3 rounded-lg bg-transparent px-1 py-1';
    case 'simple':
      return 'relative flex h-full w-full items-center gap-3 rounded-lg bg-transparent px-2 py-2';
    case 'brand':
      return 'relative flex h-full w-full items-center gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2';
    case 'dark':
      return 'relative flex h-full w-full items-center gap-2 rounded-lg bg-stone-900 px-3 py-2';
    case 'filled':
      return 'relative flex h-full w-full items-center gap-2 rounded-lg border border-stone-200/80 bg-white/85 px-3 py-2';
    case 'pill':
      return 'relative flex h-full w-full items-center gap-2 rounded-lg border border-stone-200/80 bg-white/85 px-3 py-2';
    case 'circles':
      return 'relative flex h-full w-full items-center gap-2 rounded-lg bg-transparent px-2 py-2';
    case 'stacked':
      return 'relative flex h-full w-full flex-col items-start gap-2 rounded-lg bg-transparent px-2 py-2';
    default:
      return 'relative flex h-full w-full items-center gap-2 rounded-lg border border-stone-200/80 bg-white/85 px-3 py-2';
  }
}

export function getSocialItemClasses(variant: SocialVisualVariant): string {
  switch (variant) {
    case 'minimal':
      return 'inline-flex h-8 w-8 items-center justify-center text-stone-700 transition hover:text-stone-900';
    case 'simple':
      return 'inline-flex h-8 w-8 items-center justify-center rounded-full text-stone-700 transition hover:bg-stone-100';
    case 'brand':
      return 'inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white transition hover:bg-blue-500';
    case 'dark':
      return 'inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20';
    case 'filled':
      return 'inline-flex h-8 w-8 items-center justify-center rounded-md bg-stone-800 text-white transition hover:bg-stone-700';
    case 'pill':
      return 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-700 transition hover:bg-stone-100';
    case 'circles':
    case 'stacked':
      return 'inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-700 transition hover:bg-stone-100';
    default:
      return 'inline-flex h-8 w-8 items-center justify-center rounded-md border border-stone-300 bg-white text-stone-700 transition hover:bg-stone-100';
  }
}

export function getMediaFrameContainerClasses(variant: MediaFrameVariant): string {
  switch (variant) {
    case 'premium':
      return 'relative h-full w-full overflow-hidden rounded-[28px] border border-stone-200 bg-stone-100 p-2 shadow-md';
    case 'polaroid':
      return 'relative h-full w-full overflow-hidden rounded-[20px] border border-stone-200 bg-white p-3 shadow-sm';
    case 'editorial':
      return 'relative h-full w-full overflow-hidden rounded-2xl border border-stone-200 bg-stone-50 p-3';
    case 'card':
      return 'relative h-full w-full overflow-hidden rounded-2xl border border-stone-200 bg-stone-100 shadow-sm';
    case 'square':
      return 'relative h-full w-full overflow-hidden rounded-xl border border-stone-200 bg-stone-100';
    default:
      return 'relative h-full w-full overflow-hidden rounded-lg border border-stone-200 bg-stone-100';
  }
}

export function getMediaFrameShellClasses(variant: MediaFrameVariant): string {
  switch (variant) {
    case 'premium':
      return 'relative h-full w-full overflow-visible rounded-[28px] border border-stone-200 bg-stone-100 p-2 shadow-md';
    case 'polaroid':
      return 'relative h-full w-full overflow-visible rounded-[20px] border border-stone-200 bg-white p-3 shadow-sm';
    case 'editorial':
      return 'relative h-full w-full overflow-visible rounded-2xl border border-stone-200 bg-stone-50 p-3';
    case 'card':
      return 'relative h-full w-full overflow-visible rounded-2xl border border-stone-200 bg-stone-100 shadow-sm';
    case 'square':
      return 'relative h-full w-full overflow-visible rounded-xl border border-stone-200 bg-stone-100';
    default:
      return 'relative h-full w-full overflow-visible rounded-lg border border-stone-200 bg-stone-100';
  }
}

export function getMediaFrameViewportClasses(variant: MediaFrameVariant): string {
  switch (variant) {
    case 'premium':
    case 'polaroid':
    case 'editorial':
      return 'relative h-full w-full overflow-hidden rounded-xl';
    case 'card':
      return 'relative h-full w-full overflow-hidden rounded-2xl';
    case 'square':
      return 'relative h-full w-full overflow-hidden rounded-xl';
    default:
      return 'relative h-full w-full overflow-hidden rounded-lg';
  }
}

export function getMediaFrameImageClasses(variant: MediaFrameVariant): string {
  return `h-full w-full object-cover ${variant === 'editorial' || variant === 'premium' || variant === 'polaroid' ? 'rounded-xl' : ''}`.trim();
}

export function resolveMediaFrameVariant(value: unknown): MediaFrameVariant {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === MEDIA_FRAME_VARIANTS.square) return MEDIA_FRAME_VARIANTS.square;
  if (normalized === MEDIA_FRAME_VARIANTS.card) return MEDIA_FRAME_VARIANTS.card;
  if (normalized === MEDIA_FRAME_VARIANTS.editorial) return MEDIA_FRAME_VARIANTS.editorial;
  if (normalized === MEDIA_FRAME_VARIANTS.premium) return MEDIA_FRAME_VARIANTS.premium;
  if (normalized === MEDIA_FRAME_VARIANTS.polaroid) return MEDIA_FRAME_VARIANTS.polaroid;
  return MEDIA_FRAME_VARIANTS.default;
}
